/**
 * Smart Lazy-Loading Lookup Data Hook
 *
 * Optimization Strategy:
 * - Edit Mode: Load ONLY the selected record (minimal data transport)
 * - Lazy Load: Fetch all options only when dropdown opens
 * - Download Mode: Explicitly fetch all records for export
 * - Caching: Cache results to avoid re-fetching
 */

import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Lookup Data Structure
 */
export interface LookupItem {
  id: number;
  name: string;
  [key: string]: any; // Allow additional fields from API
}

/**
 * Hook State
 */
export interface UseLazyLookupDataState {
  data: LookupItem[];
  selectedItem: LookupItem | null;
  loading: boolean;
  error: string | null;
  hasLoadedAll: boolean;
}

/**
 * Fetch Configuration
 */
export interface FetchConfig {
  getById: (id: number) => Promise<any>;
  getAll: (request?: any) => Promise<any>;
  transformData?: (response: any) => LookupItem[];
  cacheKey?: string;
}

/**
 * Cache storage (in-memory)
 */
const lookupCache = new Map<string, {
  data: LookupItem[];
  timestamp: number;
  expiresIn: number; // ms
}>();

/**
 * Default transform function - extracts id and name from response
 */
const defaultTransform = (response: any): LookupItem[] => {
  if (Array.isArray(response)) {
    return response.map(item => ({
      id: item.id || item.ID,
      name: item.name || item.Name,
      ...item,
    }));
  }
  return [];
};

/**
 * Check if cache is still valid
 */
const isCacheValid = (cacheEntry: any, expiresInMs: number = 5 * 60 * 1000): boolean => {
  return Date.now() - cacheEntry.timestamp < expiresInMs;
};

/**
 * useLazyLookupData Hook
 *
 * @param fetchConfig - Configuration object with getById and getAll functions
 * @param selectedId - Currently selected ID (for edit mode)
 * @param options - Additional options
 *
 * @returns Hook state and methods
 */
export const useLazyLookupData = (
  fetchConfig: FetchConfig,
  selectedId?: number | null,
  options: {
    initialLoad?: boolean; // If true, load selected item on mount
    cacheExpiresIn?: number; // Cache expiration in ms (default: 5 minutes)
    onError?: (error: string) => void;
  } = {}
) => {
  const { initialLoad = true, cacheExpiresIn = 5 * 60 * 1000, onError } = options;

  const [state, setState] = useState<UseLazyLookupDataState>({
    data: [],
    selectedItem: null,
    loading: false,
    error: null,
    hasLoadedAll: false,
  });

  const cacheKey = fetchConfig.cacheKey || `lookup_${Date.now()}`;
  const transform = fetchConfig.transformData || defaultTransform;
  const abortControllerRef = useRef<AbortController | null>(null);

  /**
   * Load only the selected item (Edit Mode - Minimal Data Transport)
   */
  const loadSelectedItem = useCallback(
    async (id: number) => {
      if (!id) return;

      setState(prev => ({ ...prev, loading: true, error: null }));

      try {
        const response = await fetchConfig.getById(id);

        // Apply transform function to handle entity-specific field names
        const transformedItems = transform([response]);
        const item = transformedItems[0] || {
          id: response.id || response.ID,
          name: response.name || response.Name,
          ...response,
        };

        setState(prev => ({
          ...prev,
          selectedItem: item,
          data: [item], // Show only selected item
          hasLoadedAll: false, // Reset to allow loading all options when dropdown opens
          loading: false,
        }));
      } catch (error: any) {
        const errorMsg = error.message || 'Failed to load selected item';
        setState(prev => ({
          ...prev,
          loading: false,
          error: errorMsg,
        }));
        onError?.(errorMsg);
      }
    },
    [fetchConfig, transform, onError]
  );

  /**
   * Load all records (for dropdown, download, or when user needs options)
   * Uses caching to avoid re-fetching
   */
  const loadAllRecords = useCallback(
    async (forceRefresh: boolean = false) => {
      const cacheEntry = lookupCache.get(cacheKey);

      // Return cached data if available and not expired
      if (!forceRefresh && cacheEntry && isCacheValid(cacheEntry, cacheExpiresIn)) {
        setState(prev => ({
          ...prev,
          data: cacheEntry.data,
          hasLoadedAll: true,
        }));
        return cacheEntry.data;
      }

      setState(prev => ({ ...prev, loading: true, error: null }));

      try {
        abortControllerRef.current = new AbortController();
        const response = await fetchConfig.getAll();
        const transformedData = transform(response);

        // Cache the results
        lookupCache.set(cacheKey, {
          data: transformedData,
          timestamp: Date.now(),
          expiresIn: cacheExpiresIn,
        });

        setState(prev => ({
          ...prev,
          data: transformedData,
          hasLoadedAll: true,
          loading: false,
        }));

        return transformedData;
      } catch (error: any) {
        // Don't fail if abort was triggered
        if (error.name === 'AbortError') {
          return;
        }

        const errorMsg = error.message || 'Failed to load records';
        setState(prev => ({
          ...prev,
          loading: false,
          error: errorMsg,
        }));
        onError?.(errorMsg);
      }
    },
    [cacheKey, cacheExpiresIn, fetchConfig, transform, onError]
  );

  /**
   * Lazy load on dropdown open
   * Only fetches if not already loaded
   */
  const lazyLoadOnDropdownOpen = useCallback(async () => {
    if (!state.hasLoadedAll) {
      await loadAllRecords();
    }
  }, [state.hasLoadedAll, loadAllRecords]);

  /**
   * Initial load: Load selected item if in edit mode
   */
  useEffect(() => {
    if (initialLoad && selectedId) {
      loadSelectedItem(selectedId);
    }
  }, [initialLoad, selectedId, loadSelectedItem]);

  /**
   * Clear cache (useful when data changes)
   */
  const clearCache = useCallback(() => {
    lookupCache.delete(cacheKey);
    setState(prev => ({
      ...prev,
      data: [],
      hasLoadedAll: false,
    }));
  }, [cacheKey]);

  /**
   * Get item by ID from loaded data
   */
  const getItemById = useCallback(
    (id: number): LookupItem | undefined => {
      return state.data.find(item => item.id === id);
    },
    [state.data]
  );

  /**
   * Cancel any pending requests
   */
  const cancel = useCallback(() => {
    abortControllerRef.current?.abort();
  }, []);

  return {
    // State
    data: state.data,
    selectedItem: state.selectedItem,
    loading: state.loading,
    error: state.error,
    hasLoadedAll: state.hasLoadedAll,

    // Methods
    loadSelectedItem,
    loadAllRecords,
    lazyLoadOnDropdownOpen,
    clearCache,
    getItemById,
    cancel,
  };
};

export default useLazyLookupData;
